<template>
  <main>
    <div class="error-page-glitch section">
      <div class="container">
        <div class="glitch">
          <p :data-text="code">{{ code }}</p>
        </div>
      </div>
    </div>

    <PageHeader :title="title" :subtitle="lead" />
    <section class="section">
      <div class="container">
        <div class="ck-content" v-html="text" />
      </div>
    </section>
  </main>
</template>

<script lang="ts" setup>
// This component should be kept as simple as possible and can't rely on
// anything that would require GraphQL queries or other data.
import { isValidLanguage } from '#language-negotiation/language'
import { getLanguageFromPath } from '~/helpers'

defineProps<{
  code: number
}>()

const TITLE = {
  de: 'Hoppla, da ist was schief gelaufen',
  en: 'Oops, something went wrong',
  fr: 'Oups, quelque chose a mal tourné – 500',
  it: 'Ops, qualcosa è andato storto – 500',
}

const LEAD = {
  de: 'Wir entschuldigen uns für den Systemausfall. Wir arbeiten daran.',
  en: 'Sorry for the inconvenience. We are working on it.',
  fr: 'Désolée pour le dérangement. Nous y travaillons.',
  it: 'Ci scusiamo per l’inconvenienza. Ci stiamo lavorando.',
}

const TEXT: Record<string, string> = {
  de: `
<p>Bitte besuchen Sie uns zu einem späteren Zeitpunkt noch einmal oder finden Sie Informationen über Zürich auf der Website von Schweiz Tourismus.</p>
<p>Wenn Sie eine persönliche Beratung wünschen, wenden Sie sich bitte an unsere Gästeberater:innen in der Tourist Information im Hauptbahnhof Zürich:</p>
<p>Tel: +41 44 215 40 00<br>
Email: <a href="mailto:info@zuerich.com">info@zuerich.com</a></p>
<p>Vielen Dank für Ihr Verständnis.</p>`,

  en: `
<p>Please visit us again at a later time or find information about Zurich on the Switzerland Tourism website.</p>
<p>If you would like personal assistance, please contact our guest advisors at the Tourist Information in Zurich Main Station:</p>
<p>Tel: +41 44 215 40 00<br>
Email: <a href="mailto:info@zuerich.com">info@zuerich.com</a></p>
<p>Thank you for your understanding.</p>
`,
  fr: `
<p>Veuillez revenir nous voir ultérieurement ou bien consulter les informations sur Zurich sur le site de Suisse Tourisme.</p>
<p>Si vous souhaitez un conseil personnalisé, veuillez contacter nos conseillères et conseillers à la Tourist Information de la gare centrale de Zurich&nbsp;:</p>
<p>Tél.&nbsp;: +41 44 215 40 00<br>
E-mail&nbsp;: <a href="mailto:info@zuerich.com">info@zuerich.com</a></p>
<p>Nous vous remercions de votre compréhension.</p>
`,
  it: `
<p>Vi invitiamo a tornarci a trovare in un secondo momento oppure a consultare le informazioni su Zurigo sul sito web di Svizzera Turismo.</p>
<p>Se desiderate una consulenza personalizzata, vi preghiamo di contattare i nostri consulenti per gli ospiti presso la Tourist Information alla stazione centrale di Zurigo (Zurigo HB):</p>
<p>Tel: +41 44 215 40 00<br>
Email: <a href="mailto:info@zuerich.com">info@zuerich.com</a></p>
<p>Vi ringraziamo per la vostra comprensione.</p>
`,
}

// Manually determine the language in case the useCurrentLanguage composable fails.
const url = useRequestURL()
const languageFromPath = getLanguageFromPath(url.pathname)
const langcode =
  languageFromPath && isValidLanguage(languageFromPath)
    ? languageFromPath
    : 'de'

const title = TITLE[langcode]
const lead = LEAD[langcode]
const text = TEXT[langcode]

try {
  useCDNHeaders((cdn) => {
    // Don't cache server errors. Just to be sure.
    cdn.private()
  })
} catch {
  // This response should anyway not be cached since the status code is not 200.
}
</script>

<style lang="postcss">
.error-page-glitch {
  --f-size: 40;
  --f-unit: 1vw;
  --f: calc(var(--f-size) * var(--f-unit));
  --bg: var(--color-background);
  @apply overflow-hidden;
  @screen tablet {
    --f-size: 10;
  }
}

.glitch {
  @apply mt-[0.3em];
  font-size: var(--f);
  p {
    @apply m-auto flex-1 origin-left font-bold leading-[0.75] text-scheme-invert;
    transform: scaleX(var(--scale, 1));
    animation: glitch-p 11s infinite alternate;

    &::before,
    &::after {
      --top: 0;
      --left: 0;
      --v-height: 30%;

      --n-tenth: calc(var(--f-size) * 0.1 * var(--top));
      --t-cut: calc(var(--n-tenth) / var(--f-size) * 100%);
      --b-cut: calc(var(--t-cut) + var(--v-height));

      content: attr(data-text);
      @apply absolute left-0 w-full origin-left;

      transform: translateX(calc(var(--left) * 100%));

      text-shadow:
        calc(var(--left) * -3em) 0 0em rgb(var(--color-pink)),
        calc(var(--left) * -6em) 0 0em rgb(var(--color-lime));

      background-color: var(--bg);
      clip-path: polygon(
        0% var(--t-cut),
        100% var(--t-cut),
        100% var(--b-cut),
        0% var(--b-cut)
      );
    }

    &::before {
      animation: glitch-b 1.7s infinite alternate-reverse;
    }
    &::after {
      animation: glitch-a 3.1s infinite alternate;
    }
  }

  @keyframes glitch-p {
    17% {
      --scale: 0.95;
    }
    31% {
      --scale: 1.01;
    }
    37% {
      --scale: 1.02;
    }
    47% {
      --scale: 0.97;
    }
    87% {
      --scale: 1;
    }
  }

  @keyframes glitch-a {
    10%,
    30%,
    50%,
    70%,
    90% {
      --top: 0;
      --left: 0;
    }
    0% {
      --v-height: 15%;
    }
    20% {
      --left: 0.005;
    }
    40% {
      --left: 0.01;
      --v-height: 20%;
      --top: 3;
    }
    60% {
      --left: 0.03;
      --v-height: 25%;
      --top: 6;
    }
    80% {
      --left: 0.07;
      --v-height: 5%;
      --top: 8;
    }
    100% {
      --left: 0.083;
      --v-height: 30%;
      --top: 1;
    }
  }

  @keyframes glitch-b {
    10%,
    30%,
    50%,
    70%,
    90% {
      --top: 0;
      --left: 0;
    }
    0% {
      --v-height: 15%;
      --top: 10;
    }
    20% {
      --left: -0.005;
    }
    40% {
      --left: -0.01;
      --v-height: 17%;
      --top: 3;
    }
    60% {
      --left: -0.03;
      --v-height: 35%;
      --top: 6;
    }
    80% {
      --left: -0.07;
      --v-height: 5%;
      --top: 8;
    }
    100% {
      --left: -0.083;
      --v-height: 30%;
      --top: 1;
    }
  }
}
</style>
